import React, { useMemo } from 'react';
import { CurrencyInput } from '../ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { getPersonalLiquidAssetsForRad, computeRadFundingTotals } from '../../utils/entityFundingUtils.ts';
import { calculateSharePartialDisposalTax } from '../../../../shared/src/calculation/entityDisposalTax.ts';
import { DisposalCgtEstimatePanel } from '../../features/agedCare/DisposalCgtEstimatePanel.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindAsset, FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';

type RadFundingAssetRow = ReturnType<typeof getPersonalLiquidAssetsForRad>[number];

interface AssetFundingAllocatorProps {
    factFind: DraftFactFind;
    target?: number;
    /** Allocation map: `${assetType}-${assetId}` -> gross amount to draw from that asset. */
    funding?: Record<string, number>;
    onChange: (nextMap: Record<string, number>) => void;
    cgtOverrides?: Record<string, number | null>;
    onCgtOverridesChange?: (next: Record<string, number | null>) => void;
    targetLabel?: string;
    help?: React.ReactNode;
}

/**
 * Shared "choose which assets to sell to fund $X" allocator.
 *
 * Lists the client's personal/joint liquid assets (bank, term deposits, shares, account-based pensions,
 * super) and lets them allocate an amount from each toward a target — instead of an opaque automatic
 * draw-down order. Share sales show estimated CGT (net credited toward the target). This is the same
 * mechanism the Aged Care RAD and RV entry-fee pickers use, so the granny-flat and downsizer modules stay
 * consistent. De-pastelled per the design system (white/slate surfaces, semantic accents only).
 *
 * @param {Object}   props
 * @param {Object}   props.factFind
 * @param {number}   props.target        - net amount the client needs to fund
 * @param {Object}   props.funding       - allocation map (assetId → gross amount)
 * @param {Function} props.onChange      - (nextMap) => void
 * @param {Object}   [props.cgtOverrides]
 * @param {Function} [props.onCgtOverridesChange]
 * @param {string}   [props.targetLabel='Amount to fund']
 * @param {string}   [props.help]
 */
const AssetFundingAllocator = ({
    factFind, target = 0, funding = {}, onChange,
    cgtOverrides = {}, onCgtOverridesChange,
    targetLabel = 'Amount to fund', help,
}: AssetFundingAllocatorProps) => {
    const assets = useMemo(() => getPersonalLiquidAssetsForRad(factFind), [factFind]);
    const totals = useMemo(() => computeRadFundingTotals(factFind, funding, cgtOverrides), [factFind, funding, cgtOverrides]);
    const netAllocated = totals.netTotal;
    const remaining = target - netAllocated;

    const setAmount = (assetId: string, value: number | string | undefined) => {
        const numeric = parseFloat(String(value)) || 0;
        const next: Record<string, number> = { ...funding };
        if (numeric <= 0) delete next[assetId];
        else next[assetId] = numeric;
        onChange(next);
        if (numeric <= 0 && onCgtOverridesChange && cgtOverrides?.[assetId] != null) {
            const nextO = { ...cgtOverrides };
            delete nextO[assetId];
            onCgtOverridesChange(nextO);
        }
    };

    const handleCgtOverride = (assetId: string, value: number | null) => {
        if (!onCgtOverridesChange) return;
        const next = { ...cgtOverrides };
        if (value == null) delete next[assetId];
        else next[assetId] = value;
        onCgtOverridesChange(next);
    };

    if (assets.length === 0) {
        return <p className="text-sm text-slate-500 italic">No liquid assets are recorded in your profile to fund this from.</p>;
    }

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h4 className="text-sm font-semibold text-gray-900">Which assets will you use?</h4>
                    <p className="text-xs text-gray-600">{help || 'Choose how much to draw from each asset. Share sales show estimated capital gains tax; the net amount counts toward what you need.'}</p>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{remaining > 0 ? 'Still to fund' : 'Fully funded'}</p>
                    <p className={`text-lg font-bold ${remaining > 0.5 ? 'text-amber-700' : 'text-green-700'}`}>{formatCurrency(Math.max(0, remaining))}</p>
                </div>
            </div>

            <div className="space-y-3">
                {assets.map((asset: RadFundingAssetRow) => {
                    const amount = funding[asset.assetId] || 0;
                    const available = parseFloat(String(asset.value)) || 0;
                    const isShares = asset.typeId === 'shares';
                    return (
                        <div key={asset.assetId} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <label className="block text-sm font-medium text-gray-700">
                                {asset.description || asset.typeName}
                                <span className="block text-xs font-normal text-slate-500">Available {formatCurrency(available)}{isShares && asset.costBase ? ' · CGT deducted from proceeds' : ''}</span>
                            </label>
                            <div className="flex items-center gap-2 mt-1">
                                <input type="range" min="0" max={available} value={amount} onChange={(e) => setAmount(asset.assetId, e.target.value)} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
                                <div className="w-32">
                                    <CurrencyInput
                                        value={amount || ''}
                                        onChange={(v) => setAmount(asset.assetId, v)}
                                        placeholder="0"
                                        className=""
                                        ariaLabel={`Amount from ${asset.description || asset.typeName}`}
                                    />
                                </div>
                            </div>
                            {isShares && amount > 0 && asset.costBase && (
                                <div className="mt-2">
                                    <DisposalCgtEstimatePanel
                                        factFind={factFind}
                                        taxSummary={calculateSharePartialDisposalTax(factFind as unknown as FactFind, asset as unknown as FactFindAsset, amount, { trustDisposal: 'distribute' })}
                                        overrideValue={cgtOverrides?.[asset.assetId]}
                                        onOverrideChange={(value) => handleCgtOverride(asset.assetId, value)}
                                        asset={asset}
                                        showNetProceedsLine
                                        saleAmount={amount}
                                        netProceedsLabel="Net toward what you need (after estimated CGT)"
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="pt-3 border-t border-slate-200 flex justify-between text-sm font-bold">
                <span>{targetLabel}: {formatCurrency(target)}</span>
                <span className={netAllocated > target + 0.5 ? 'text-red-600' : 'text-gray-800'}>Allocated (net): {formatCurrency(netAllocated)}</span>
            </div>
        </div>
    );
};

export default AssetFundingAllocator;
